/**
 * ModalKit — the ONE look for every create/edit popup in the portal.
 *
 * The audit found the billing-document modals shipped as raw inputs with
 * placeholder-as-label, mystery date fields, fixed-width rows that crushed on
 * phones, and a header that scrolled away with the body. This kit fixes the
 * shell once so every dialog gets it:
 *
 *   - Bottom sheet on phones (slides to the bottom edge, drag-handle bar),
 *     centred card on bigger screens.
 *   - Fixed header (icon chip + title + one-line subtitle) and fixed footer;
 *     only the BODY scrolls.
 *   - Real labels above every field (Field), quiet section headers (Section),
 *     one input style (INPUT / SELECT), pill buttons (BtnPrimary / BtnGhost).
 *   - Focus uses `outline`, never ring-* (ring is box-shadow-based and dies
 *     under card shadows — repo gotcha).
 *
 *   <Modal icon={Truck} title="New challan" subtitle="…" onClose={…}
 *          footer={<><BtnGhost onClick={…}>Cancel</BtnGhost>
 *                    <BtnPrimary onClick={save}>Create</BtnPrimary></>}>
 *     <Section title="Customer">
 *       <Field label="Customer name"><input className={INPUT} … /></Field>
 *     </Section>
 *   </Modal>
 */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';

export const INPUT =
  'w-full h-11 border border-line rounded-xl px-3.5 text-sm text-ink bg-white ' +
  'placeholder:text-gray-400 focus:outline focus:outline-2 focus:outline-primary/25 ' +
  'focus:border-primary/60 transition-colors';

export const SELECT = INPUT + ' appearance-none pr-8 bg-no-repeat bg-[right_0.75rem_center] ' +
  'bg-[url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27%236b7280%27 stroke-width=%272.5%27%3E%3Cpath d=%27m6 9 6 6 6-6%27/%3E%3C/svg%3E")]';

export const Field = ({ label, hint, className = '', children }) => (
  <label className={`block min-w-0 ${className}`}>
    <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
      {label}
    </span>
    {children}
    {hint && <span className="block text-[11px] text-gray-400 mt-1">{hint}</span>}
  </label>
);

export const Section = ({ title, hint, children }) => (
  <div>
    <div className="flex items-baseline justify-between mb-2.5">
      <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400">{title}</span>
      {hint && <span className="text-[11px] text-gray-400">{hint}</span>}
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

// One line-item of a document — a quiet card, with its remove control in the
// corner instead of squeezed into the row.
export const LineCard = ({ onRemove, removable = true, children }) => (
  <div className="relative rounded-xl border border-line bg-bone/50 p-3 pr-10 space-y-2.5">
    {removable && onRemove && (
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove this line"
        title="Remove this line"
        className="absolute top-2 right-2 w-7 h-7 rounded-full grid place-items-center text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
      >
        <X size={14} />
      </button>
    )}
    {children}
  </div>
);

export const AddLineButton = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full rounded-xl border border-dashed border-line py-2.5 text-sm font-medium text-primary hover:border-primary/60 hover:bg-primary/5 transition-colors"
  >
    {children}
  </button>
);

export const ErrorBanner = ({ children }) =>
  children ? (
    <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">{children}</div>
  ) : null;

export const BtnGhost = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="h-11 px-5 rounded-full border border-line text-sm font-semibold text-ink hover:bg-gray-50 transition-colors"
  >
    {children}
  </button>
);

export const BtnPrimary = ({ onClick, busy, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    className="h-11 px-6 rounded-full bg-primary text-white text-sm font-bold hover:bg-primary-dark disabled:opacity-60 transition-colors inline-flex items-center gap-2"
  >
    {busy && <Loader2 size={15} className="animate-spin" />}
    {children}
  </button>
);

export const Modal = ({ icon: Icon, title, subtitle, onClose, footer, maxW = 'sm:max-w-2xl', children }) => {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  // PORTALLED to <body>: page shells use transforms (motion transitions),
  // which turn `fixed` into "fixed to the transformed ancestor" — the mobile
  // bottom sheet was floating 200px above the bottom edge because of it.
  return createPortal(
    <div
      className="fixed inset-0 z-[10000] bg-ink/40 backdrop-blur-[2px] flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={`bg-white w-full ${maxW} max-h-[92dvh] flex flex-col rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag-handle affordance — phones only, where this is a sheet. */}
        <div className="sm:hidden pt-2.5 flex justify-center">
          <span className="w-9 h-1 rounded-full bg-gray-200" />
        </div>

        <div className="flex items-center gap-3 px-5 py-4 border-b border-line flex-shrink-0">
          {Icon && (
            <span className="w-10 h-10 rounded-xl grid place-items-center flex-shrink-0"
              style={{ backgroundColor: '#FDF3F3', color: '#B40A0A' }}>
              <Icon size={18} />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-bold text-ink leading-tight">{title}</h2>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5 truncate">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full grid place-items-center text-gray-400 hover:text-ink hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">{children}</div>

        {footer && (
          <div className="px-5 py-4 border-t border-line bg-bone/60 flex items-center justify-end gap-3 flex-wrap flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
